import {useId} from 'react';
import {ConfirmFormRow} from '../confirm-form-row';
import styles from './confirm-company-information-section.module.css';
import companyRegistrationPageStyles from '@/features/company-registration/styles.module.css';
import {type FormSchema} from '@/features/company-registration/form';
import {useMasterData} from '@/features/company-registration/components/master-data-provider';

type Props = Pick<FormSchema, 'companyInfo'>;

export function ConfirmCompanyInformationSection({companyInfo}: Props) {
  const id = useId();
  const formTitleId = `${id}-formTitle`;
  const data = useMasterData();

  return (
    <section
      aria-labelledby={formTitleId}
      className={companyRegistrationPageStyles.container}
    >
      <div className={companyRegistrationPageStyles.header}>
        <h2
          className={companyRegistrationPageStyles.formTitle}
          id={formTitleId}
        >
          企業情報
        </h2>
      </div>

      <div className={companyRegistrationPageStyles.rows}>
        <ConfirmFormRow labelText='法人名'>
          <p>
            {companyInfo.corporateName}
            {companyInfo.isSoleProprietor ? (
              <span className={styles.soloLabel}>（個人事業主）</span>
            ) : null}
          </p>
        </ConfirmFormRow>

        <ConfirmFormRow labelText='法人名（カナ）'>
          {companyInfo.corporateNameKana}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='代表者名'>
          {companyInfo.presidentName}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='代表者役職'>
          {companyInfo.presidentTitle}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='会社設立日'>
          {companyInfo.foundationYear && companyInfo.foundationMonth
            ? `${companyInfo.foundationYear}年${companyInfo.foundationMonth}月`
            : null}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='本社所在地'>
          <p>
            {companyInfo.zip.slice(0, 3)}-{companyInfo.zip.slice(3)}
          </p>
          <p>{companyInfo.address}</p>
          {companyInfo.address2 ? <p>{companyInfo.address2}</p> : null}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='代表電話番号'>
          {companyInfo.phoneNumber}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='上場区分'>
          {
            data?.ipoKbn.find(
              (item) => item.id === Number.parseInt(companyInfo.ipoKbn, 10),
            )?.name
          }
        </ConfirmFormRow>

        <ConfirmFormRow labelText='証券コード'>
          {companyInfo.securityCode}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='資本金'>
          {companyInfo.capital}万円
        </ConfirmFormRow>

        <ConfirmFormRow labelText='社員数'>
          {companyInfo.employees}人
        </ConfirmFormRow>

        <ConfirmFormRow labelText='サイトURL'>
          {companyInfo.websiteUrl}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='業種区分'>
          {
            data?.industryKbn.find(
              (item) =>
                item.id === Number.parseInt(companyInfo.industryKbnId, 10),
            )?.name
          }
        </ConfirmFormRow>

        <ConfirmFormRow labelText='企業カテゴリ'>
          {companyInfo.companyCategoryIds.map((companyCategoryId) => (
            <p key={companyCategoryId}>
              {
                data?.companyCategory.find(
                  (category) => category.id === companyCategoryId,
                )?.name
              }
            </p>
          ))}
        </ConfirmFormRow>

        <ConfirmFormRow labelText='ビジネスカテゴリ'>
          {companyInfo.businessCategoryLv2Ids
            .filter((businessCategoryLv2Id) => businessCategoryLv2Id !== '')
            .map((businessCategoryLv2Id) => (
              <p key={businessCategoryLv2Id}>
                {
                  data?.businessCategory
                    .flatMap((category) => category.lv2)
                    .find(
                      (c) =>
                        c?.id === Number.parseInt(businessCategoryLv2Id, 10),
                    )?.name
                }
              </p>
            ))}
        </ConfirmFormRow>
      </div>
    </section>
  );
}
